import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Table, UploadSimple, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const TEMPLATE = `title,category,date,start_time,end_time,location,postcode,pay_rate,staff_required,description
Warehouse Operative,Warehousing,2026-08-20,08:00,16:00,Manchester,M17 1AA,12.5,3,Standard warehouse shift
Care Assistant,Healthcare,2026-08-21,07:00,15:00,Leeds,LS1 4BT,13.75,2,Assist with morning care
Waiter,Hospitality,2026-08-22,17:00,23:00,Manchester,M2 3AA,11.5,4,Evening service`;

function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return { header: [], rows: [] };
  const header = lines[0].split(",").map(s => s.trim());
  const rows = lines.slice(1).map(line => {
    // simple CSV split (no quoted commas)
    const cells = line.split(",").map(s => s.trim());
    const obj = {};
    header.forEach((h, i) => { obj[h] = cells[i] ?? ""; });
    return obj;
  });
  return { header, rows };
}

function toApiRow(r) {
  return {
    title: r.title || "",
    category: r.category || "Warehousing",
    date: r.date || "",
    start_time: r.start_time || "09:00",
    end_time: r.end_time || "17:00",
    location: r.location || "",
    postcode: r.postcode || "",
    pay_rate: Number(r.pay_rate) || 0,
    staff_required: Number(r.staff_required) || 1,
    description: r.description || "",
    break_minutes: Number(r.break_minutes) || 30,
    dress_code: r.dress_code || "",
    instructions: r.instructions || "",
  };
}

export default function BulkJobs() {
  const [text, setText] = useState(TEMPLATE);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const runPreview = async () => {
    const parsed = parseCsv(text);
    if (parsed.rows.length === 0) return toast.error("Nothing to import");
    setBusy(true);
    try {
      const r = await api.post("/company/jobs/bulk", { rows: parsed.rows.map(toApiRow), dry_run: true });
      setPreview({ ...r.data, total: parsed.rows.length });
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };
  const runImport = async () => {
    const parsed = parseCsv(text);
    setBusy(true);
    try {
      const r = await api.post("/company/jobs/bulk", { rows: parsed.rows.map(toApiRow), dry_run: false });
      toast.success(`Created ${r.data.accepted} job(s). ${r.data.rejected.length} rejected.`);
      nav("/company/jobs");
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl space-y-5">
      <div className="flex items-center gap-3">
        <Table size={28} className="text-blue-600" />
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Bulk import jobs</h1>
      </div>
      <p className="text-slate-600">Paste a CSV of shifts. Columns: <code className="text-xs bg-slate-100 px-1.5 rounded">title, category, date (YYYY-MM-DD), start_time, end_time, location, postcode, pay_rate, staff_required, description</code></p>
      <Textarea data-testid="bulk-textarea" rows={14} value={text} onChange={(e)=>setText(e.target.value)} className="font-mono text-sm" />
      <div className="flex flex-wrap gap-2">
        <Button data-testid="bulk-preview-btn" variant="outline" onClick={runPreview} disabled={busy}>Preview</Button>
        <Button data-testid="bulk-import-btn" onClick={runImport} disabled={busy || !preview} className="bg-blue-600 hover:bg-blue-700">
          <UploadSimple size={16} className="mr-1.5"/> {busy ? "Working…" : `Import ${preview?.accepted ?? 0} jobs`}
        </Button>
      </div>
      {preview && (
        <div className="card-tactile p-5 space-y-3">
          <div className="flex items-center gap-4 text-sm">
            <span className="inline-flex items-center gap-1.5 text-emerald-700"><CheckCircle size={16} weight="fill"/> {preview.accepted} valid</span>
            <span className="inline-flex items-center gap-1.5 text-red-600"><WarningCircle size={16} weight="fill"/> {preview.rejected.length} rejected</span>
            <span className="text-slate-500">of {preview.total} rows</span>
          </div>
          {preview.rejected.length > 0 && (
            <div className="text-sm">
              <div className="label-caps mb-1">Rejected rows</div>
              <ul className="space-y-1">
                {preview.rejected.map((r) => (
                  <li key={r.row} className="text-red-700">Row {r.row+1} · {r.title || "(no title)"} — {r.errors.join(", ")}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
